/**
 * Channel-identities tab (SPEC screen 8). Strictly read-only: identities are
 * written by channel intake's match-or-create service, and the contract
 * publishes no mutation for them. `channel_id` is the durable key;
 * `channel_name` is a display snapshot resolved from the channel lookup, so
 * it is rendered as a label and never used to identify a channel.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";

import { DataTable, dateTimeCell } from "@/components/data-table";

import type { ChannelIdentity, MatchedBy } from "../api";
import { useChannelIdentities } from "../queries";

export interface ChannelIdentitiesPanelProps {
  partyId: string;
  canRead?: boolean;
}

export function ChannelIdentitiesPanel({ partyId, canRead = true }: ChannelIdentitiesPanelProps) {
  const { t } = useTranslation();
  const query = useChannelIdentities(partyId, canRead);

  const columns = useMemo<ColumnDef<ChannelIdentity>[]>(
    () => [
      {
        accessorKey: "channel_name",
        id: "channel_name",
        header: t("partners.channels.columns.channel"),
        enableSorting: false,
        cell: (ctx) => <span dir="auto">{ctx.getValue<string>()}</span>,
      },
      {
        accessorKey: "external_id",
        id: "external_id",
        header: t("partners.channels.columns.externalId"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => (
          <span dir="ltr" className="font-mono text-xs">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "matched_by",
        id: "matched_by",
        header: t("partners.channels.columns.matchedBy"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<MatchedBy>();
          return <Badge variant="outline">{t(`partners.matchedBy.${value}`)}</Badge>;
        },
      },
      {
        accessorKey: "last_seen_at",
        id: "last_seen_at",
        header: t("partners.channels.columns.lastSeen"),
        enableSorting: false,
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
      {
        accessorKey: "created_at",
        id: "created_at",
        header: t("partners.channels.columns.firstSeen"),
        enableSorting: false,
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
    ],
    [t],
  );

  if (!canRead) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          {t("errors.forbidden", { permission: "partners.channel_identity.read" })}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>{t("partners.channels.title")}</h2>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">{t("partners.channels.readOnly")}</p>
        {query.isError ? (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
            <span>{t("partners.channels.loadError")}</span>
            <Button type="button" size="sm" variant="outline" onClick={() => void query.refetch()}>
              {t("actions.retry")}
            </Button>
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={query.data ?? []}
            getRowId={(row) => row.id}
            showColumnsMenu={false}
            emptyMessage={t("partners.channels.empty")}
          />
        )}
      </CardContent>
    </Card>
  );
}
